import type { SupplierBidFeeBounds, SupplierBidFeeItem, SupplierBidQuote } from '@/api/bidding/supplier-bid'
import { sortBiddingFeeItems } from './bidding-fee-display'

/** 最低消费和上下限快照属于金额限制，不作为单价参与计算。 */
export function isBiddingFeeBoundVariant(variant?: string): boolean {
  return ['MIN', 'MAX', 'AIR_M'].includes(String(variant || '').trim().toUpperCase())
}

/** 按费用类识别干线段，基本费用标记用于另行判断原生报价规则。 */
export function isBiddingMainlineFee(
  fee: Pick<SupplierBidFeeItem, 'transportMode' | 'feeCategory'>
): boolean {
  return ['FCL', 'LCL', 'AIR'].includes(fee.transportMode)
    && ['干线', 'MAINLINE', 'BASIC', 'MAIN'].includes(String(fee.feeCategory || '').trim().toUpperCase())
}

/** 仅干线基本费用保留拼箱最低收费和空运 M 原生报价列。 */
export function isBiddingMainlineBasicFee(
  fee: Pick<SupplierBidFeeItem, 'transportMode' | 'basicFee' | 'feeCategory'>
): boolean {
  return isBiddingMainlineFee(fee) && fee.basicFee === true
}

/** 拼箱/空运干线基础费用统一恢复最低/M，包括曾开启 Min/Max 的历史快照。 */
export function isBiddingStandaloneMinimumFee(fee: SupplierBidFeeItem): boolean {
  if (!isBiddingMainlineBasicFee(fee)) return false
  const variant = String(fee.quoteVariant || '').trim().toUpperCase()
  return (fee.transportMode === 'LCL' && variant === 'MIN')
    || (fee.transportMode === 'AIR' && variant === 'AIR_M')
}

/** 原始报价列包含恢复的最低/M，主费用不再展示历史最高列。 */
export function isBiddingFeeQuoteColumn(fee: SupplierBidFeeItem): boolean {
  return !isBiddingFeeBoundVariant(fee.quoteVariant) || isBiddingStandaloneMinimumFee(fee)
}

/** 同一费用的单价档共用上下限，全程费用按运输段隔离，普通历史费用保持原键。 */
export function biddingFeeBoundsKey(fee: SupplierBidFeeItem): string {
  const key = `${fee.transportMode}:${fee.feeId || fee.id}`
  return fee.transportSegment ? `${key}:${fee.transportSegment}` : key
}

/** 返回已启用上下限或需承接其他历史限制的费用组，最低/M独立列不重复追加上下限。 */
export function biddingFeeBoundsGroups(fees: SupplierBidFeeItem[]): Set<string> {
  return new Set(fees.filter(fee => !isBiddingMainlineBasicFee(fee)
    && (fee.minMaxEnabled || isBiddingFeeBoundVariant(fee.quoteVariant)))
    .map(biddingFeeBoundsKey))
}

function optionalAmount(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') return undefined
  const amount = Number(value)
  return Number.isFinite(amount) ? amount : undefined
}

/**
 * 读取独立上下限；只有该组未保存新配置时才回退旧 MIN/MAX/M 金额。
 * 显式空对象保留用户的清空操作，避免旧最低消费在刷新后复活。
 */
export function getBiddingFeeBounds(
  quote: SupplierBidQuote,
  fee: SupplierBidFeeItem,
  fees: SupplierBidFeeItem[]
): SupplierBidFeeBounds {
  const key = biddingFeeBoundsKey(fee)
  if (Object.prototype.hasOwnProperty.call(quote.feeBounds || {}, key)) {
    const bounds = quote.feeBounds?.[key]
    return { min: optionalAmount(bounds?.min), max: optionalAmount(bounds?.max) }
  }
  const group = fees.filter(item => biddingFeeBoundsKey(item) === key)
  const amount = (variant: string) => {
    const item = group.find(candidate => String(candidate.quoteVariant || '').toUpperCase() === variant)
    return item ? optionalAmount(quote.feeQuotes?.[String(item.id)]) : undefined
  }
  return { min: amount('MIN') ?? amount('AIR_M'), max: amount('MAX') }
}

/** 历史发布缺少最低快照时，仅新增界面列描述，不伪造可提交的快照 ID。 */
export interface BiddingPriceFee extends SupplierBidFeeItem {
  minimumFallback?: boolean
}

/** 费用组不存在原生最低快照时，通过已有 feeBounds.min 承接最低输入。 */
function needsMinimumFallback(fee: SupplierBidFeeItem, fees: SupplierBidFeeItem[]): boolean {
  return isBiddingMainlineBasicFee(fee) && fee.transportMode !== 'FCL'
    && !fees.some(item => biddingFeeBoundsKey(item) === biddingFeeBoundsKey(fee)
      && isBiddingStandaloneMinimumFee(item))
}

/** 原生最低键（含显式清空）优先；旧 Min 仅用于尚未填写原生最低的历史报价。 */
export function getBiddingFeeQuoteValue(quote: SupplierBidQuote, fee: BiddingPriceFee): number | undefined {
  if (fee.minimumFallback) return optionalAmount(quote.feeBounds?.[biddingFeeBoundsKey(fee)]?.min)
  if (isBiddingStandaloneMinimumFee(fee)
    && !Object.prototype.hasOwnProperty.call(quote.feeQuotes || {}, String(fee.id))) {
    return optionalAmount(quote.feeBounds?.[biddingFeeBoundsKey(fee)]?.min)
  }
  return optionalAmount(quote.feeQuotes?.[String(fee.id)])
}

/** 最低兼容列只写真实快照键或已有费用组 min；主费用 Max 不再提交。 */
export function setBiddingFeeQuoteValue(quote: SupplierBidQuote, fee: BiddingPriceFee, value: unknown): void {
  if (fee.minimumFallback) {
    const bounds = quote.feeBounds || (quote.feeBounds = {})
    bounds[biddingFeeBoundsKey(fee)] = { min: optionalAmount(value) }
  } else {
    quote.feeQuotes[String(fee.id)] = optionalAmount(value)
    if (isBiddingStandaloneMinimumFee(fee)) delete quote.feeBounds?.[biddingFeeBoundsKey(fee)]
  }
}

/** 在移除旧上下限前把最低迁入真实 MIN/M 键，显式清空不得被旧值覆盖。 */
export function normalizeBiddingFeeQuotes(quote: SupplierBidQuote, fees: SupplierBidFeeItem[]): SupplierBidQuote['feeQuotes'] {
  const result = Object.fromEntries(Object.entries(quote.feeQuotes || {})
    .map(([key, value]) => [key, optionalAmount(value)]))
  fees.filter(isBiddingStandaloneMinimumFee).forEach(fee => {
    result[String(fee.id)] = getBiddingFeeQuoteValue(quote, fee)
  })
  return result
}

/** 将详情或导入中的上下限还原为独立可编辑状态，保持各报价行对象隔离。 */
export function normalizeBiddingFeeBounds(
  quote: SupplierBidQuote,
  fees: SupplierBidFeeItem[]
): Record<string, SupplierBidFeeBounds> {
  const result: Record<string, SupplierBidFeeBounds> = {}
  const mainlineGroups = new Map(fees.filter(isBiddingMainlineBasicFee).map(fee => [biddingFeeBoundsKey(fee), fee]))
  Object.entries(quote.feeBounds || {}).forEach(([key, bounds]) => {
    const mainlineFee = mainlineGroups.get(key)
    if (mainlineFee) {
      // 仅缺少原生最低快照的历史拼箱/空运保留 min；真实最低由 feeQuotes 承接。
      if (needsMinimumFallback(mainlineFee, fees)) result[key] = { min: optionalAmount(bounds?.min) }
      return
    }
    result[key] = { min: optionalAmount(bounds?.min), max: optionalAmount(bounds?.max) }
  })
  fees.filter(fee => isBiddingFeeBoundVariant(fee.quoteVariant)
    && !mainlineGroups.has(biddingFeeBoundsKey(fee))).forEach(fee => {
    const key = biddingFeeBoundsKey(fee)
    if (!Object.prototype.hasOwnProperty.call(result, key)) {
      result[key] = getBiddingFeeBounds(quote, fee, fees)
    }
  })
  return result
}

/** 统一列顺序：拼箱单价后最低，空运 M 在 N 前；其他费用保留已有次序。 */
export function biddingPriceFees(fees: SupplierBidFeeItem[]): BiddingPriceFee[] {
  const sorted = sortBiddingFeeItems(fees.filter(isBiddingFeeQuoteColumn))
  const groups = new Map<string, SupplierBidFeeItem[]>()
  sorted.forEach(fee => {
    const key = biddingFeeBoundsKey(fee)
    const items = groups.get(key) || []
    items.push(fee)
    groups.set(key, items)
  })
  return [...groups.values()].flatMap(items => {
    const first = items[0]!
    if (!isBiddingMainlineBasicFee(first) || first.transportMode === 'FCL') return items
    const prices = items.filter(fee => !isBiddingStandaloneMinimumFee(fee))
    const minimum = items.find(isBiddingStandaloneMinimumFee)
    const source = prices[0] || first
    const minimumColumn: BiddingPriceFee = minimum || {
      ...source,
      quoteVariant: first.transportMode === 'AIR' ? 'AIR_M' : 'MIN',
      required: first.transportMode === 'AIR' && prices.some(fee => fee.required),
      minimumFallback: true
    }
    if (first.transportMode === 'AIR') return [minimumColumn, ...prices]
    const baseIndex = prices.findIndex(fee => !fee.quoteVariant || fee.quoteVariant === 'BASE')
    const insertIndex = baseIndex < 0 ? 1 : baseIndex + 1
    return [...prices.slice(0, insertIndex), minimumColumn, ...prices.slice(insertIndex)]
  })
}

/** 独立 UI 列键不会成为提交 feeQuotes 的键，避免覆盖原单价。 */
export function biddingPriceFeeColumnKey(fee: BiddingPriceFee): string {
  return fee.minimumFallback ? `minimum:${biddingFeeBoundsKey(fee)}` : String(fee.id)
}

/** 空运 M 是按票最低消费，不能继承重量档的单价计费单位。 */
export function biddingPriceFeeUnit(fee: BiddingPriceFee, locale: string): string {
  return isBiddingStandaloneMinimumFee(fee) && fee.transportMode === 'AIR'
    ? (locale.toLowerCase().startsWith('en') ? 'Per Bill' : '票') : String(fee.billingUnit || '').trim()
}

/** 部分列导入只改实际出现的一侧；空格清空该侧，缺列保留原值。 */
export function mergeImportedFeeBounds(
  current: SupplierBidQuote['feeBounds'],
  imported: SupplierBidQuote['feeBounds'],
  fields?: Record<string, Array<'min' | 'max'>>
): Record<string, SupplierBidFeeBounds> {
  const merged = Object.fromEntries(Object.entries(current || {}).map(([key, value]) => [key, { ...value }]))
  Object.entries(imported || {}).forEach(([key, value]) => {
    const present = fields?.[key] || ['min', 'max'] as const
    const bounds = merged[key] || (merged[key] = {})
    present.forEach(bound => { bounds[bound] = value[bound] ?? undefined })
  })
  return merged
}
